import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Circle, Sun, Moon } from 'lucide-react';

// Import all slides
import TitleSlide from './slides/TitleSlide';
import OutlineSlide from './slides/OutlineSlide';
import IntroSlide from './slides/IntroSlide';
import GroyperSlide from './slides/GroyperSlide';
import SwedenSlide from './slides/SwedenSlide';
import PolandHungarySlide from './slides/PolandHungarySlide';
import IsraelSlide from './slides/IsraelSlide';
import DoubleStandardsSlide from './slides/DoubleStandardsSlide';
import ConclusionSlide from './slides/ConclusionSlide';
import DiscussionSlide from './slides/DiscussionSlide';
import SourcesSlide from './slides/SourcesSlide';
import EndSlide from './slides/EndSlide';

import './App.css';
import './styles/imageConfig.css';  // Centralized image control

// Design resolution - slides are designed for this size
const DESIGN_WIDTH = 1280;
const DESIGN_HEIGHT = 720;

const slides = [
  { component: TitleSlide, title: 'Titel' },
  { component: OutlineSlide, title: 'Gliederung' },
  { component: IntroSlide, title: 'Einleitung' },
  { component: GroyperSlide, title: 'Strategie (1)' },
  { component: SwedenSlide, title: 'Strategie (2)' },
  { component: PolandHungarySlide, title: 'Polen & Ungarn' },
  { component: IsraelSlide, title: 'Israel' },
  { component: DoubleStandardsSlide, title: 'Doppelmoral' },
  { component: ConclusionSlide, title: 'Fazit' },
  { component: DiscussionSlide, title: 'Diskussion' },
  { component: SourcesSlide, title: 'Quellen' },
  { component: EndSlide, title: 'Ende' },
];

function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [scale, setScale] = useState(1);
  const viewportRef = useRef(null);

  // Calculate scale to fit slides in viewport
  useEffect(() => {
    const calculateScale = () => {
      if (!viewportRef.current) return;
      
      const viewport = viewportRef.current;
      // Minimal padding for maximum content space
      const availableWidth = viewport.clientWidth - 20;
      const availableHeight = viewport.clientHeight - 20;
      
      const scaleX = availableWidth / DESIGN_WIDTH;
      const scaleY = availableHeight / DESIGN_HEIGHT;
      
      // Use the smaller scale to ensure content fits both dimensions
      const newScale = Math.min(scaleX, scaleY, 1.5); // Allow up to 1.5x for larger screens
      setScale(Math.max(0.4, newScale)); // Min 0.4x for very small windows
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    
    // Recalculate on fullscreen change
    document.addEventListener('fullscreenchange', calculateScale);
    
    return () => {
      window.removeEventListener('resize', calculateScale);
      document.removeEventListener('fullscreenchange', calculateScale);
    };
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length) {
      setDirection(index > currentSlide ? 1 : -1);
      setCurrentSlide(index);
    }
  }, [currentSlide]);

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      } else if (e.key === 'n' || e.key === 'N') {
        setShowNotes(prev => !prev);
      } else if (e.key === 't' || e.key === 'T') {
        // Theme toggle via keyboard (T)
        toggleTheme();
      } else if (e.key === 'Home') {
        goToSlide(0);
      } else if (e.key === 'End') {
        goToSlide(slides.length - 1);
      } else if (e.key >= '1' && e.key <= '9') {
        goToSlide(parseInt(e.key) - 1);
      } else if (e.key === '0') {
        goToSlide(9);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, goToSlide]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    }),
  };

  const CurrentSlideComponent = slides[currentSlide].component;

  return (
    <div className="presentation-container">
      {/* Animated background */}
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      {/* Progress bar */}
      <motion.div 
        className="progress-bar"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: (currentSlide + 1) / slides.length }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      />

      {/* Main slide area */}
      <div className="slide-viewport" ref={viewportRef}>
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="slide-wrapper"
            style={{
              width: DESIGN_WIDTH,
              height: DESIGN_HEIGHT,
              transform: `scale(${scale})`,
              transformOrigin: 'center center'
            }}
          >
            <CurrentSlideComponent showNotes={showNotes} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation controls */}
      <div className="navigation-controls">
        <div className="slide-dots">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              whileHover={{ scale: 1.3 }}
              whileTap={{ scale: 0.9 }}
            >
              <Circle size={index === currentSlide ? 10 : 8} fill={index === currentSlide ? "currentColor" : "none"} />
            </motion.button>
          ))}
        </div>
      </div>

      {/* Slide counter */}
      <div className="slide-counter">
        <span className="current">{String(currentSlide + 1).padStart(2, '0')}</span>
        <span className="separator">/</span>
        <span className="total">{String(slides.length).padStart(2, '0')}</span>
      </div>

      {/* Control panel removed: theme toggled via 'T' keybind */}

      {/* Keyboard hints */}
      <div className="keyboard-hints">
        <span>← → Navigieren</span>
        <span>F Vollbild</span>
        <span>N Zusatzinfos</span>
        <span>T Theme</span>
      </div>
    </div>
  );
}

export default App;
